import type { EntityId } from './entity';
import { makeSphereArena, type SphereArena } from './sphereArena';
import { sphereLineOfSight, type SpherePoint } from './sphereLineOfSight';

export const SPHERE_TICKS_PER_SECOND = 20;
export const SPHERE_COUNTDOWN_TICKS = 3 * SPHERE_TICKS_PER_SECOND;
export const SPHERE_MATCH_TICKS = 10 * 60 * SPHERE_TICKS_PER_SECOND;
export const SPHERE_SUDDEN_DEATH_TICKS = 60 * SPHERE_TICKS_PER_SECOND;
export const SPHERE_RESULT_TICKS = 60 * SPHERE_TICKS_PER_SECOND;
export const SPHERE_MAX_HEALTH = 100;
export const SPHERE_MAX_MANA = 100;
export const SPHERE_MAX_RANGE = 12;
export const SPHERE_POTION_COOLDOWN_TICKS = 10 * SPHERE_TICKS_PER_SECOND;
export const SPHERE_POISON_INTERVAL_TICKS = 2 * SPHERE_TICKS_PER_SECOND;
export const SPHERE_POISON_TICK_COUNT = 10;
export const SPHERE_POISON_DAMAGE = 3;
export const SPHERE_POISON_DURATION_TICKS =
  SPHERE_POISON_INTERVAL_TICKS * SPHERE_POISON_TICK_COUNT;
export const SPHERE_CAST_TICKS_PER_CIRCLE = SPHERE_TICKS_PER_SECOND / 2;

export type SphereSpellId =
  | 'magic-arrow'
  | 'poison'
  | 'lightning'
  | 'flame-strike'
  | 'greater-heal'
  | 'cure';

export type SpherePotionId = 'greater-heal' | 'mana';

export interface SphereSpellDefinition {
  id: SphereSpellId;
  name: string;
  words: string;
  mana: number;
  circle: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  castTicks: number;
  target: 'opponent' | 'self';
  damage?: readonly [number, number];
  healing?: readonly [number, number];
}

function sphereSpell(
  definition: Omit<SphereSpellDefinition, 'castTicks'>,
): SphereSpellDefinition {
  return {
    ...definition,
    castTicks: definition.circle * SPHERE_CAST_TICKS_PER_CIRCLE,
  };
}

export const SPHERE_SPELLS: Readonly<Record<SphereSpellId, SphereSpellDefinition>> = {
  'magic-arrow': sphereSpell({
    id: 'magic-arrow',
    name: 'Magic Arrow',
    words: 'In Por Ylem',
    mana: 4,
    circle: 1,
    target: 'opponent',
    damage: [3, 5],
  }),
  poison: sphereSpell({
    id: 'poison',
    name: 'Poison',
    words: 'In Nox',
    mana: 9,
    circle: 3,
    target: 'opponent',
  }),
  lightning: sphereSpell({
    id: 'lightning',
    name: 'Lightning',
    words: 'Por Ort Grav',
    mana: 11,
    circle: 4,
    target: 'opponent',
    damage: [17, 22],
  }),
  'flame-strike': sphereSpell({
    id: 'flame-strike',
    name: 'Flame Strike',
    words: 'Kal Vas Flam',
    mana: 40,
    circle: 7,
    target: 'opponent',
    damage: [38, 46],
  }),
  'greater-heal': sphereSpell({
    id: 'greater-heal',
    name: 'Greater Heal',
    words: 'In Vas Mani',
    mana: 11,
    circle: 4,
    target: 'self',
    healing: [24, 30],
  }),
  cure: sphereSpell({
    id: 'cure',
    name: 'Cure',
    words: 'An Nox',
    mana: 6,
    circle: 2,
    target: 'self',
  }),
};

export type SpherePhase = 'loading' | 'countdown' | 'fighting' | 'paused' | 'result' | 'settled';
export type SphereOutcome =
  | { kind: 'win'; winner: EntityId; loser: EntityId; reason: 'death' | 'timeout' | 'forfeit' }
  | { kind: 'draw'; reason: 'mutual-death' | 'timeout' | 'disconnect' }
  | { kind: 'cancelled'; reason: 'storm' | 'shutdown' | 'infrastructure' };

interface ActiveCast {
  spell: SphereSpellId;
  completesAt: number;
}

interface PoisonState {
  nextTick: number;
  ticksRemaining: number;
}

export interface SphereParticipantState {
  health: number;
  mana: number;
  ready: boolean;
  cast?: ActiveCast;
  poison?: PoisonState;
  potionReadyAt: number;
}

export interface SphereMatch {
  readonly players: readonly [EntityId, EntityId];
  readonly arena: SphereArena;
  readonly participant: Map<EntityId, SphereParticipantState>;
  phase: SpherePhase;
  tick: number;
  combatTicks: number;
  countdownTicksRemaining: number;
  resultTicksRemaining: number;
  outcome?: SphereOutcome;
}

export type SpherePositions = ReadonlyMap<EntityId, SpherePoint>;

export type SphereMatchCommand =
  | { kind: 'ready'; player: EntityId }
  | { kind: 'cast'; player: EntityId; spell: SphereSpellId }
  | { kind: 'potion'; player: EntityId; potion: SpherePotionId }
  | { kind: 'cancel'; player: EntityId };

export type SphereFizzleReason =
  | 'not-fighting'
  | 'busy'
  | 'mana'
  | 'range'
  | 'line-of-sight'
  | 'cancelled'
  | 'interrupted'
  | 'sudden-death'
  | 'poisoned'
  | 'cooldown'
  | 'full'
  | 'stale-match'
  | 'stale-command';

export type SphereEffect =
  | { kind: 'announcement'; text: string }
  | { kind: 'cast-start'; player: EntityId; spell: SphereSpellId; words: string }
  | { kind: 'fizzle'; player: EntityId; spell?: SphereSpellId; reason: SphereFizzleReason }
  // A targeted cast refused before it ever began by the pre-cast line-of-sight gate.
  // Distinct from a `fizzle` because the cast never started.
  | { kind: 'blocked'; player: EntityId; spell: SphereSpellId; reason: 'line-of-sight' }
  | {
      kind: 'damage';
      source: EntityId;
      target: EntityId;
      spell?: SphereSpellId;
      amount: number;
      poisonTicksRemaining?: number;
    }
  | { kind: 'heal'; player: EntityId; source: 'spell' | 'potion'; amount: number }
  | { kind: 'poison'; source: EntityId; target: EntityId; refreshed: boolean }
  | { kind: 'cure'; player: EntityId; removed: boolean }
  | { kind: 'potion'; player: EntityId; potion: SpherePotionId; amount: number }
  | { kind: 'result'; outcome: SphereOutcome };

export interface SpherePlayerView {
  active: boolean;
  phase: SpherePhase;
  health: number;
  mana: number;
  opponentHealth: number;
  /** This player is poisoned - renders the green health bar and blocks healing. */
  poisoned: boolean;
  /** The opponent is poisoned - drives their green health bar on this client. */
  opponentPoisoned: boolean;
  remainingTicks: number;
  potionCooldownTicks: number;
  suddenDeath: boolean;
  available: Record<SphereSpellId | `potion-${SpherePotionId}`, boolean>;
  outcome?: SphereOutcome;
}

export function launchSphereMatch(opts: {
  players: readonly [EntityId, EntityId];
  arena?: SphereArena;
}): SphereMatch {
  const participant = new Map<EntityId, SphereParticipantState>();
  for (const player of opts.players) {
    participant.set(player, {
      health: SPHERE_MAX_HEALTH,
      mana: SPHERE_MAX_MANA,
      ready: false,
      potionReadyAt: 0,
    });
  }
  return {
    players: [opts.players[0], opts.players[1]],
    arena: opts.arena ?? makeSphereArena(),
    participant,
    phase: 'loading',
    tick: 0,
    combatTicks: 0,
    countdownTicksRemaining: SPHERE_COUNTDOWN_TICKS,
    resultTicksRemaining: 0,
  };
}

function stateOf(match: SphereMatch, player: EntityId): SphereParticipantState | undefined {
  return match.participant.get(player);
}

function opponentOf(match: SphereMatch, player: EntityId): EntityId | undefined {
  if (match.players[0] === player) return match.players[1];
  if (match.players[1] === player) return match.players[0];
  return undefined;
}

function isSuddenDeath(match: SphereMatch): boolean {
  return match.combatTicks >= SPHERE_MATCH_TICKS - SPHERE_SUDDEN_DEATH_TICKS;
}

function targetVerdict(
  match: SphereMatch,
  source: EntityId,
  target: EntityId,
  positions: SpherePositions,
): 'ok' | 'range' | 'line-of-sight' {
  const from = positions.get(source);
  const to = positions.get(target);
  if (
    !from ||
    !to ||
    Math.max(Math.abs(from.x - to.x), Math.abs(from.y - to.y)) > SPHERE_MAX_RANGE
  ) {
    return 'range';
  }
  return sphereLineOfSight(from, to, match.arena.walls) ? 'ok' : 'line-of-sight';
}

function fizzle(
  player: EntityId,
  reason: SphereFizzleReason,
  spell?: SphereSpellId,
): SphereEffect[] {
  return [{ kind: 'fizzle', player, ...(spell ? { spell } : {}), reason }];
}

/** Apply one player command through the match's public, server-authoritative seam. */
export function commandSphereMatch(
  match: SphereMatch,
  command: SphereMatchCommand,
  positions: SpherePositions,
): SphereEffect[] {
  const state = stateOf(match, command.player);
  if (!state) return [];

  if (command.kind === 'ready') {
    if (match.phase !== 'loading') return [];
    state.ready = true;
    if (match.players.every((player) => stateOf(match, player)?.ready === true)) {
      match.phase = 'countdown';
      match.countdownTicksRemaining = SPHERE_COUNTDOWN_TICKS;
      return [{ kind: 'announcement', text: '3' }];
    }
    return [];
  }

  if (match.phase !== 'fighting') {
    if (command.kind === 'cast') return fizzle(command.player, 'not-fighting', command.spell);
    return fizzle(command.player, 'not-fighting');
  }

  if (command.kind === 'cancel') {
    if (!state.cast) return fizzle(command.player, 'busy');
    const spell = state.cast.spell;
    delete state.cast;
    return fizzle(command.player, 'cancelled', spell);
  }

  if (command.kind === 'potion') {
    if (match.tick < state.potionReadyAt) return fizzle(command.player, 'cooldown');
    if (command.potion === 'greater-heal') {
      if (isSuddenDeath(match)) return fizzle(command.player, 'sudden-death');
      // Poison suppresses every heal source until it is cured or expires.
      if (state.poison) return fizzle(command.player, 'poisoned');
      if (state.health >= SPHERE_MAX_HEALTH) return fizzle(command.player, 'full');
      const amount = Math.min(30, SPHERE_MAX_HEALTH - state.health);
      state.health += amount;
      state.potionReadyAt = match.tick + SPHERE_POTION_COOLDOWN_TICKS;
      return [
        { kind: 'potion', player: command.player, potion: command.potion, amount },
        { kind: 'heal', player: command.player, source: 'potion', amount },
      ];
    }
    if (state.mana >= SPHERE_MAX_MANA) return fizzle(command.player, 'full');
    const amount = Math.min(50, SPHERE_MAX_MANA - state.mana);
    state.mana += amount;
    state.potionReadyAt = match.tick + SPHERE_POTION_COOLDOWN_TICKS;
    return [{ kind: 'potion', player: command.player, potion: command.potion, amount }];
  }

  const spell = SPHERE_SPELLS[command.spell];
  if (spell.id === 'greater-heal' && isSuddenDeath(match)) {
    return fizzle(command.player, 'sudden-death', command.spell);
  }
  if (state.mana < spell.mana) return fizzle(command.player, 'mana', command.spell);
  if (spell.target === 'opponent') {
    const opponent = opponentOf(match, command.player);
    if (opponent === undefined) return [];
    const verdict = targetVerdict(match, command.player, opponent, positions);
    // Pre-cast line-of-sight gate: an already-hidden target aborts the cast cleanly
    // here, before any words of power, mana, or cooldown. It is reported as a
    // `blocked` (caster-only) effect rather than the shared `fizzle` the opponent
    // sees, so the wasted-cast experience never begins. A mid-cast LOS loss (the
    // target hides after the cast legitimately started) still fizzles in
    // stepSphereMatch, which is the intended interrupt and is left untouched.
    if (verdict === 'line-of-sight') {
      return [{ kind: 'blocked', player: command.player, spell: command.spell, reason: verdict }];
    }
    if (verdict !== 'ok') return fizzle(command.player, verdict, command.spell);
  }
  state.cast = { spell: command.spell, completesAt: match.tick + spell.castTicks };
  return [
    { kind: 'cast-start', player: command.player, spell: command.spell, words: spell.words },
  ];
}

function rollInclusive(range: readonly [number, number], rng: () => number): number {
  const [min, max] = range;
  return min + Math.floor(Math.min(0.999999999999, Math.max(0, rng())) * (max - min + 1));
}

function conclude(match: SphereMatch, outcome: SphereOutcome): SphereEffect[] {
  match.phase = 'result';
  match.outcome = outcome;
  match.resultTicksRemaining = SPHERE_RESULT_TICKS;
  for (const state of match.participant.values()) delete state.cast;
  return [{ kind: 'result', outcome }];
}

/** Pause a live match without advancing any countdown, cast, poison, or clock. */
export function pauseSphereMatch(match: SphereMatch): boolean {
  if (match.phase !== 'countdown' && match.phase !== 'fighting') return false;
  match.phase = 'paused';
  return true;
}

/** Resume a match at the phase it occupied before a reconnect pause. */
export function resumeSphereMatch(match: SphereMatch, phase: 'countdown' | 'fighting'): boolean {
  if (match.phase !== 'paused') return false;
  match.phase = phase;
  return true;
}

/** Resolve an externally decided result through the same 60-second result phase. */
export function concludeSphereMatch(match: SphereMatch, outcome: SphereOutcome): SphereEffect[] {
  if (match.phase === 'result' || match.phase === 'settled') return [];
  return conclude(match, outcome);
}

function timeoutOutcome(match: SphereMatch): SphereOutcome {
  const a = stateOf(match, match.players[0])!;
  const b = stateOf(match, match.players[1])!;
  if (a.health === b.health) return { kind: 'draw', reason: 'timeout' };
  return a.health > b.health
    ? { kind: 'win', winner: match.players[0], loser: match.players[1], reason: 'timeout' }
    : { kind: 'win', winner: match.players[1], loser: match.players[0], reason: 'timeout' };
}

/** Advance one authoritative 50ms match tick and return its observable effects. */
export function stepSphereMatch(
  match: SphereMatch,
  positions: SpherePositions,
  rng: () => number,
): SphereEffect[] {
  if (match.phase === 'loading' || match.phase === 'paused' || match.phase === 'settled') return [];
  match.tick += 1;
  if (match.phase === 'countdown') {
    match.countdownTicksRemaining -= 1;
    if (match.countdownTicksRemaining === 40) return [{ kind: 'announcement', text: '2' }];
    if (match.countdownTicksRemaining === 20) return [{ kind: 'announcement', text: '1' }];
    if (match.countdownTicksRemaining <= 0) {
      match.phase = 'fighting';
      return [{ kind: 'announcement', text: 'FIGHT!' }];
    }
    return [];
  }
  if (match.phase === 'result') {
    match.resultTicksRemaining -= 1;
    if (match.resultTicksRemaining <= 0) match.phase = 'settled';
    return [];
  }
  if (match.phase !== 'fighting') return [];

  match.combatTicks += 1;
  const effects: SphereEffect[] = [];
  const deltas = new Map<EntityId, number>();
  const landedOffense = new Set<EntityId>();
  const pendingPoisons = new Map<EntityId, EntityId>();
  const pendingCures = new Set<EntityId>();
  const pendingSpellHeals = new Map<EntityId, number>();

  // Gather every completion before applying any effect. This is the simultaneous
  // resolution rule: participant iteration order cannot decide an exchange.
  for (const player of match.players) {
    const state = stateOf(match, player)!;
    const cast = state.cast;
    if (!cast || cast.completesAt > match.tick) continue;
    delete state.cast;
    const spell = SPHERE_SPELLS[cast.spell];
    if (spell.id === 'greater-heal' && isSuddenDeath(match)) {
      effects.push(...fizzle(player, 'sudden-death', spell.id));
      continue;
    }
    // Poison blocks the completed heal with the normal shared fizzle.
    if (spell.id === 'greater-heal' && state.poison) {
      effects.push(...fizzle(player, 'poisoned', spell.id));
      continue;
    }
    const target = spell.target === 'self' ? player : opponentOf(match, player)!;
    if (spell.target === 'opponent') {
      const verdict = targetVerdict(match, player, target, positions);
      if (verdict !== 'ok') {
        effects.push(...fizzle(player, verdict, spell.id));
        continue;
      }
    }
    state.mana -= spell.mana;
    if (spell.damage) {
      const amount = rollInclusive(spell.damage, rng);
      deltas.set(target, (deltas.get(target) ?? 0) - amount);
      effects.push({ kind: 'damage', source: player, target, spell: spell.id, amount });
      landedOffense.add(target);
    } else if (spell.id === 'poison') {
      pendingPoisons.set(target, player);
      landedOffense.add(target);
    } else if (spell.healing) {
      const amount = rollInclusive(spell.healing, rng);
      deltas.set(player, (deltas.get(player) ?? 0) + amount);
      pendingSpellHeals.set(player, amount);
    } else if (spell.id === 'cure') {
      pendingCures.add(player);
    }
  }

  // Status changes resolve as one batch too. Cure wins a same-tick conflict with
  // Poison, so reversing participant order cannot change the resulting status.
  for (const player of match.players) {
    const source = pendingPoisons.get(player);
    if (source === undefined) continue;
    const state = stateOf(match, player)!;
    const refreshed = state.poison !== undefined;
    state.poison = {
      nextTick: match.tick + SPHERE_POISON_INTERVAL_TICKS,
      ticksRemaining: SPHERE_POISON_TICK_COUNT,
    };
    effects.push({ kind: 'poison', source, target: player, refreshed });
  }
  for (const player of match.players) {
    if (!pendingCures.has(player)) continue;
    const state = stateOf(match, player)!;
    const removed = state.poison !== undefined;
    delete state.poison;
    effects.push({ kind: 'cure', player, removed });
  }

  // Due poison damage joins the same health batch and, like any incoming damage,
  // disrupts the poisoned player's in-progress cast: the tick registers the player
  // as landed offense so the shared interrupt pass below fizzles their cast.
  for (const player of match.players) {
    const state = stateOf(match, player)!;
    if (!state.poison || state.poison.nextTick > match.tick) continue;
    state.poison.nextTick += SPHERE_POISON_INTERVAL_TICKS;
    state.poison.ticksRemaining -= 1;
    deltas.set(player, (deltas.get(player) ?? 0) - SPHERE_POISON_DAMAGE);
    landedOffense.add(player);
    effects.push({
      kind: 'damage',
      source: opponentOf(match, player)!,
      target: player,
      spell: 'poison',
      amount: SPHERE_POISON_DAMAGE,
      poisonTicksRemaining: state.poison.ticksRemaining,
    });
    if (state.poison.ticksRemaining <= 0) delete state.poison;
  }

  // A successful offensive resolution interrupts only a cast that did not also
  // complete on this tick. Mana was not spent by the interrupted cast.
  for (const target of landedOffense) {
    const targetState = stateOf(match, target)!;
    if (!targetState.cast) continue;
    const spell = targetState.cast.spell;
    delete targetState.cast;
    effects.push(...fizzle(target, 'interrupted', spell));
  }

  for (const player of match.players) {
    const state = stateOf(match, player)!;
    const healthBefore = state.health;
    const delta = deltas.get(player) ?? 0;
    const healthAfter = Math.max(0, Math.min(SPHERE_MAX_HEALTH, healthBefore + delta));
    state.health = healthAfter;
    const rolledHeal = pendingSpellHeals.get(player);
    if (rolledHeal !== undefined) {
      const healthWithoutHeal = Math.max(
        0,
        Math.min(SPHERE_MAX_HEALTH, healthBefore + delta - rolledHeal),
      );
      effects.push({
        kind: 'heal',
        player,
        source: 'spell',
        amount: healthAfter - healthWithoutHeal,
      });
    }
  }

  const dead = match.players.filter((player) => stateOf(match, player)!.health <= 0);
  if (dead.length === 2) {
    effects.push(...conclude(match, { kind: 'draw', reason: 'mutual-death' }));
    return effects;
  }
  if (dead.length === 1) {
    const loser = dead[0]!;
    effects.push(
      ...conclude(match, {
        kind: 'win',
        winner: opponentOf(match, loser)!,
        loser,
        reason: 'death',
      }),
    );
    return effects;
  }

  if (match.combatTicks % SPHERE_TICKS_PER_SECOND === 0) {
    for (const state of match.participant.values()) {
      state.mana = Math.min(SPHERE_MAX_MANA, state.mana + 1);
    }
  }
  if (match.combatTicks === SPHERE_MATCH_TICKS - SPHERE_SUDDEN_DEATH_TICKS) {
    effects.push({ kind: 'announcement', text: 'SUDDEN DEATH - HEALING DISABLED' });
  }
  if (match.combatTicks >= SPHERE_MATCH_TICKS) {
    effects.push(...conclude(match, timeoutOutcome(match)));
  }
  return effects;
}

export function spherePlayerView(match: SphereMatch, player: EntityId): SpherePlayerView {
  const state = stateOf(match, player);
  const opponent = opponentOf(match, player);
  const opponentState = opponent === undefined ? undefined : stateOf(match, opponent);
  if (!state || !opponentState) {
    throw new Error(`Sphere Duel: entity ${player} is not a participant`);
  }
  const fighting = match.phase === 'fighting';
  const suddenDeath = fighting && isSuddenDeath(match);
  const poisoned = state.poison !== undefined;
  const available = {} as SpherePlayerView['available'];
  for (const spell of Object.values(SPHERE_SPELLS)) {
    const healBlocked = spell.id === 'greater-heal' && suddenDeath;
    available[spell.id] = fighting && state.mana >= spell.mana && !healBlocked;
  }
  const potionReady = fighting && match.tick >= state.potionReadyAt;
  available['potion-greater-heal'] =
    potionReady && !suddenDeath && !poisoned && state.health < SPHERE_MAX_HEALTH;
  available['potion-mana'] = potionReady && state.mana < SPHERE_MAX_MANA;
  return {
    active: match.phase !== 'settled',
    phase: match.phase,
    health: state.health,
    mana: state.mana,
    opponentHealth: opponentState.health,
    poisoned,
    opponentPoisoned: opponentState.poison !== undefined,
    remainingTicks:
      match.phase === 'countdown'
        ? match.countdownTicksRemaining
        : match.phase === 'result'
          ? match.resultTicksRemaining
          : Math.max(0, SPHERE_MATCH_TICKS - match.combatTicks),
    potionCooldownTicks: Math.max(0, state.potionReadyAt - match.tick),
    suddenDeath,
    available,
    ...(match.outcome ? { outcome: match.outcome } : {}),
  };
}
